import { useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/components/ui/Toast';
import { useUpdateItemThumbnail } from '@/hooks/useItems';

interface ItemThumbnailDropButtonProps {
  itemId: number;
  className?: string;
  onSuccess?: () => void;
}

function getFirstImageFile(fileList: FileList | null): File | null {
  if (!fileList || fileList.length === 0) {
    return null;
  }

  return Array.from(fileList).find((file) => file.type.startsWith('image/')) ?? fileList[0] ?? null;
}

export function ItemThumbnailDropButton({
  itemId,
  className = '',
  onSuccess,
}: ItemThumbnailDropButtonProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const { toast } = useToast();
  const updateThumbnail = useUpdateItemThumbnail();

  const handleSelectedFile = async (file: File | null): Promise<void> => {
    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Image required.',
        description: 'Please drop or choose an image file.',
        tone: 'error',
      });
      return;
    }

    try {
      await updateThumbnail.mutateAsync({ itemId, file });
      toast({
        title: 'Thumbnail updated.',
        description: 'The dropped image is now the item thumbnail.',
        tone: 'success',
      });
      onSuccess?.();
    } catch (error) {
      toast({
        title: "Couldn't update thumbnail.",
        description: error instanceof Error ? error.message : 'Please try again.',
        tone: 'error',
      });
    }
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = getFirstImageFile(event.target.files);
    void handleSelectedFile(file);
    event.target.value = '';
  };

  const handleDragOver = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();

    if (updateThumbnail.isPending) {
      return;
    }

    event.dataTransfer.dropEffect = 'copy';
    setIsDragActive(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLButtonElement>) => {
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
      return;
    }

    setIsDragActive(false);
  };

  const handleDrop = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();

    if (updateThumbnail.isPending) {
      return;
    }

    setIsDragActive(false);
    void handleSelectedFile(getFirstImageFile(event.dataTransfer.files));
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="sr-only"
        onChange={handleInputChange}
        data-ai-id="item-detail-thumbnail-upload-input"
      />

      <Button
        type="button"
        size="sm"
        variant="secondary"
        loading={updateThumbnail.isPending}
        disabled={itemId <= 0}
        className={[
          'shrink-0 border-dashed',
          isDragActive
            ? 'border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-50'
            : 'border-slate-400 bg-slate-50 text-slate-700 hover:bg-slate-100',
          className,
        ].join(' ')}
        onClick={() => inputRef.current?.click()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        title="Drop an image file here or click to choose a thumbnail"
        data-ai-id="item-detail-thumbnail-upload-button"
        aiIdPrefix="item-detail-thumbnail-upload-button"
      >
        {updateThumbnail.isPending ? 'Updating...' : isDragActive ? 'Release to set' : 'Drop thumb'}
      </Button>
    </>
  );
}
